"use client";

import React from "react";
import { Check, X, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ClampedText } from "@/components/shared/ClampedText";
import { useTimeEntryHooks } from "@/features/time-entries/hooks/time-entry-hooks";
import { toast } from "sonner";

import type { EditableTimeEntryDescriptionProps } from "../types";

/**
 * Inline-editable Work Description for a time entry. When a description exists
 * it renders as clamped text with a hover pencil; when it's missing the
 * fallback message is paired with an "Add description" button that flips the
 * same textarea open in place — so the value can be fixed without leaving the
 * card. Persists via useTimeEntryHooks().useUpdate() (description is a plain
 * string column on time_entries). Local mirror re-seeds on entryId so it stays
 * correct when navigating between entries.
 *
 * Exported so the legacy TimeEntryDetails/DescriptionCard reuses the exact same
 * editor (StandardTimeEntry and Time_Entries both expose id + description).
 */
export function EditableTimeEntryDescription({
  entryId,
  description,
  onRefresh,
}: EditableTimeEntryDescriptionProps) {
  const { mutateAsync, isPending } = useTimeEntryHooks().useUpdate();

  const initial = description ?? null;
  const [value, setValue] = React.useState<string | null>(initial);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(initial ?? "");

  // Re-seed when the underlying entry changes.
  React.useEffect(() => {
    setValue(initial);
    if (!isEditing) setDraft(initial ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, entryId]);

  const startEdit = () => {
    setDraft(value ?? "");
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(value ?? "");
    setIsEditing(false);
  };

  const save = async () => {
    const next = draft.trim();
    if (next === (value ?? "")) {
      setIsEditing(false);
      return;
    }
    try {
      await mutateAsync({ id: entryId, description: next });
      setValue(next || null);
      setIsEditing(false);
      toast.success("Description updated");
      onRefresh?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update description");
    }
  };

  if (isEditing) {
    return (
      <div>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Describe the work performed…"
          rows={4}
          autoFocus
          disabled={isPending}
          className="text-sm"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void save();
            }
          }}
        />
        <div className="mt-2 flex items-center gap-2">
          <Button type="button" size="sm" onClick={() => void save()} disabled={isPending}>
            <Check className="h-4 w-4 mr-1" />
            {isPending ? "Saving…" : "Save"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={isPending}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <span className="text-xs text-gray-400">⌘/Ctrl+Enter to save · Esc to cancel</span>
        </div>
      </div>
    );
  }

  if (value) {
    return (
      <div className="group/desc flex items-start justify-between gap-2">
        <ClampedText text={value} lines={4} className="text-sm text-card-foreground" />
        <button
          type="button"
          onClick={startEdit}
          className="opacity-0 group-hover/desc:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 mt-0.5 flex-shrink-0"
          title="Edit description"
          aria-label="Edit work description"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <Button type="button" size="sm" variant="outline" onClick={startEdit} disabled={isPending}>
      <Plus className="h-4 w-4 mr-1" />
      Add description
    </Button>
  );
}
